import { create } from 'zustand';
import { importTrackingService } from '../services/import-tracking-service';
import type {
  ImportDetailTab,
  ImportOrder,
  ImportOrderStatus,
  ImportOrderItem,
  CostBreakdown,
  ImportDocument,
  TrackingEvent,
  ImportAuditLog,
  ImportDashboardStats,
  ImportsByStatusChart,
  MonthlyImportTrend,
  TopSupplier,
  CostVarianceAlert,
  LandedCostInput,
  LandedCostResult,
  ImportSupplier,
  CreateImportOrderInput,
  UpdateImportOrderInput,
  CreateImportItemInput,
  CreateTrackingEventInput,
  CreateImportSupplierInput,
  ContainerType,
  Container,
  ContainerItem,
  CreateContainerInput,
  UpdateContainerInput,
  CreateContainerItemInput,
  UpdateContainerItemInput,
  CreateContainerTypeInput,
  BulkPositionUpdate,
  AutoCalculateResult,
  AvailableContainer,
  PortSummary,
} from '../types/import-tracking.types';

interface ImportTrackingState {
  // ── UI ──
  activeDetailTab: ImportDetailTab;
  setActiveDetailTab: (tab: ImportDetailTab) => void;

  // ── General loading ──
  isLoading: boolean;

  // ── Orders List ──
  orders: ImportOrder[];
  ordersTotal: number;
  ordersPage: number;
  ordersTotalPages: number;
  ordersSearch: string;
  ordersStatusFilter: ImportOrderStatus | '';
  ordersSupplierFilter: string;
  ordersDateFrom: string;
  ordersDateTo: string;
  isLoadingOrders: boolean;

  setOrdersSearch: (search: string) => void;
  setOrdersStatusFilter: (status: ImportOrderStatus | '') => void;
  setOrdersSupplierFilter: (supplierId: string) => void;
  setOrdersDateRange: (from: string, to: string) => void;

  fetchOrders: (params?: {
    page?: number;
    /** Etapa: cotizaciones esperando respuesta, o embarques en curso. */
    stage?: 'quote' | 'operational';
    /** @deprecated Es el origen, no el estado actual. Preferir `stage`. */
    isQuote?: boolean;
  }) => Promise<void>;
  createOrder: (data: CreateImportOrderInput) => Promise<ImportOrder>;
  deleteOrder: (id: string) => Promise<void>;

  // ── Current Order Detail ──
  currentOrder: ImportOrder | null;
  isLoadingOrder: boolean;
  fetchOrder: (id: string) => Promise<void>;
  updateOrder: (id: string, data: UpdateImportOrderInput) => Promise<void>;

  // ── Items ──
  orderItems: ImportOrderItem[];
  isLoadingItems: boolean;
  fetchOrderItems: (orderId: string) => Promise<void>;
  addOrderItem: (orderId: string, data: CreateImportItemInput) => Promise<void>;
  updateOrderItem: (
    orderId: string,
    itemId: string,
    data: Partial<CreateImportItemInput>
  ) => Promise<void>;
  deleteOrderItem: (orderId: string, itemId: string) => Promise<void>;

  // ── Costs ──
  costBreakdown: CostBreakdown | null;
  isLoadingCosts: boolean;
  fetchCostBreakdown: (orderId: string) => Promise<void>;
  updateCosts: (orderId: string, data: Partial<CostBreakdown>) => Promise<void>;

  // ── Documents ──
  documents: ImportDocument[];
  isLoadingDocuments: boolean;
  fetchDocuments: (orderId: string) => Promise<void>;
  uploadDocument: (orderId: string, file: File, type: string, name: string) => Promise<void>;
  deleteDocument: (orderId: string, docId: string) => Promise<void>;

  // ── Tracking Events ──
  trackingEvents: TrackingEvent[];
  isLoadingTracking: boolean;
  fetchTrackingEvents: (orderId: string) => Promise<void>;
  addTrackingEvent: (orderId: string, data: CreateTrackingEventInput) => Promise<void>;

  // ── Audit Logs ──
  auditLogs: ImportAuditLog[];
  isLoadingAuditLogs: boolean;
  fetchAuditLogs: (orderId: string) => Promise<void>;

  // ── Dashboard / KPIs ──
  dashboardStats: ImportDashboardStats | null;
  importsByStatus: ImportsByStatusChart[];
  monthlyTrends: MonthlyImportTrend[];
  topSuppliers: TopSupplier[];
  costVarianceAlerts: CostVarianceAlert[];
  isLoadingDashboard: boolean;
  fetchDashboard: () => Promise<void>;

  // ── Landed Cost Calculator ──
  landedCostResult: LandedCostResult | null;
  isCalculating: boolean;
  calculateLandedCost: (data: LandedCostInput) => Promise<LandedCostResult | null>;
  clearLandedCostResult: () => void;

  // ── Suppliers ──
  suppliers: ImportSupplier[];
  isLoadingSuppliers: boolean;
  fetchSuppliers: () => Promise<void>;
  createSupplier: (data: CreateImportSupplierInput) => Promise<void>;
  updateSupplier: (id: string, data: Partial<CreateImportSupplierInput>) => Promise<void>;
  deleteSupplier: (id: string) => Promise<void>;

  // ── Container Types ──
  containerTypes: ContainerType[];
  isLoadingContainerTypes: boolean;
  fetchContainerTypes: () => Promise<void>;
  createContainerType: (data: CreateContainerTypeInput) => Promise<ContainerType>;
  deleteContainerType: (id: string) => Promise<void>;

  // ── Containers ──
  containers: Container[];
  currentContainer: Container | null;
  isLoadingContainers: boolean;
  fetchOrderContainers: (orderId: string) => Promise<void>;
  fetchContainer: (id: string) => Promise<void>;
  createContainer: (orderId: string, data: CreateContainerInput) => Promise<Container>;
  updateContainer: (id: string, data: UpdateContainerInput) => Promise<void>;
  deleteContainer: (id: string) => Promise<void>;

  // ── Container Items ──
  containerItems: ContainerItem[];
  isLoadingContainerItems: boolean;
  fetchContainerItems: (containerId: string) => Promise<void>;
  addContainerItem: (containerId: string, data: CreateContainerItemInput) => Promise<ContainerItem>;
  updateContainerItem: (
    containerId: string,
    itemId: string,
    data: UpdateContainerItemInput
  ) => Promise<void>;
  deleteContainerItem: (containerId: string, itemId: string) => Promise<void>;

  // ── Container Positions ──
  isSavingPositions: boolean;
  saveContainerPositions: (containerId: string, data: BulkPositionUpdate) => Promise<void>;

  // ── Auto Calculate ──
  autoCalculateResult: AutoCalculateResult | null;
  isAutoCalculating: boolean;
  autoCalculateImport: (
    orderId: string,
    params?: {
      insuranceRate?: number;
      defaultDutyRate?: number;
      taxRate?: number;
      internacionRate?: number;
    }
  ) => Promise<AutoCalculateResult | null>;
  clearAutoCalculateResult: () => void;

  // ── Convert Quote to Order ──
  convertToOrder: (orderId: string) => Promise<void>;

  // ── Available Containers (Availability Map) ──
  availableContainers: AvailableContainer[];
  portSummaries: PortSummary[];
  loadingAvailableContainers: boolean;
  loadAvailableContainers: () => Promise<void>;
  loadPortSummaries: () => Promise<void>;

  // ── Error ──
  error: string | null;
  clearError: () => void;
}

export const useImportTrackingStore = create<ImportTrackingState>((set, get) => ({
  // ── UI ──
  activeDetailTab: 'general',
  setActiveDetailTab: (tab) => set({ activeDetailTab: tab }),

  // ── General loading ──
  isLoading: false,

  // ── Orders List ──
  orders: [],
  ordersTotal: 0,
  ordersPage: 1,
  ordersTotalPages: 1,
  ordersSearch: '',
  ordersStatusFilter: '',
  ordersSupplierFilter: '',
  ordersDateFrom: '',
  ordersDateTo: '',
  isLoadingOrders: false,

  setOrdersSearch: (search) => set({ ordersSearch: search }),
  setOrdersStatusFilter: (status) => set({ ordersStatusFilter: status }),
  setOrdersSupplierFilter: (supplierId) => set({ ordersSupplierFilter: supplierId }),
  setOrdersDateRange: (from, to) => set({ ordersDateFrom: from, ordersDateTo: to }),

  fetchOrders: async (params = {}) => {
    set({ isLoadingOrders: true, error: null });
    try {
      const state = get();
      const data = await importTrackingService.getOrders({
        search: state.ordersSearch || undefined,
        status: state.ordersStatusFilter || undefined,
        freightProviderId: state.ordersSupplierFilter || undefined,
        dateFrom: state.ordersDateFrom || undefined,
        dateTo: state.ordersDateTo || undefined,
        page: params.page ?? 1,
        limit: 25,
        stage: params.stage,
        isQuote: params.isQuote,
      });
      set({
        orders: data.orders,
        ordersTotal: data.total,
        ordersPage: data.page,
        ordersTotalPages: data.totalPages,
        isLoadingOrders: false,
      });
    } catch (err: any) {
      console.error('[import-tracking-store] fetchOrders failed:', err);
      set({
        error: err?.response?.data?.error || 'Error cargando órdenes',
        isLoadingOrders: false,
      });
    }
  },

  createOrder: async (data) => {
    set({ error: null });
    try {
      const order = await importTrackingService.createOrder(data);
      // El re-fetch lo dispara el caller en onSuccess con el filtro isQuote
      // adecuado — hacerlo aca con fetchOrders() sin filtro mezclaba orders
      // y quotes en la tabla de Importaciones.
      return order;
    } catch (err: any) {
      set({ error: err?.response?.data?.error || 'Error creando orden' });
      throw err;
    }
  },

  deleteOrder: async (id) => {
    try {
      await importTrackingService.deleteOrder(id);
      set((s) => ({ orders: s.orders.filter((o) => o.id !== id) }));
    } catch (err: any) {
      set({ error: err?.response?.data?.error || 'Error eliminando orden' });
    }
  },

  // ── Current Order Detail ──
  currentOrder: null,
  isLoadingOrder: false,

  fetchOrder: async (id) => {
    set({ isLoadingOrder: true, error: null });
    try {
      const order = await importTrackingService.getOrder(id);
      set({ currentOrder: order, isLoadingOrder: false });
    } catch (err: any) {
      set({
        error: err?.response?.data?.error || 'Error cargando orden',
        isLoadingOrder: false,
      });
    }
  },

  updateOrder: async (id, data) => {
    set({ error: null });
    try {
      const updated = await importTrackingService.updateOrder(id, data);
      set({ currentOrder: updated });
    } catch (err: any) {
      set({ error: err?.response?.data?.error || 'Error actualizando orden' });
      throw err;
    }
  },

  // ── Items ──
  orderItems: [],
  isLoadingItems: false,

  fetchOrderItems: async (orderId) => {
    set({ isLoadingItems: true, error: null });
    try {
      const items = await importTrackingService.getOrderItems(orderId);
      set({ orderItems: items, isLoadingItems: false });
    } catch (err: any) {
      set({
        error: err?.response?.data?.error || 'Error cargando productos',
        isLoadingItems: false,
      });
    }
  },

  addOrderItem: async (orderId, data) => {
    set({ error: null });
    try {
      await importTrackingService.addOrderItem(orderId, data);
      await get().fetchOrderItems(orderId);
    } catch (err: any) {
      set({ error: err?.response?.data?.error || 'Error agregando producto' });
      throw err;
    }
  },

  updateOrderItem: async (orderId, itemId, data) => {
    set({ error: null });
    try {
      await importTrackingService.updateOrderItem(orderId, itemId, data);
      await get().fetchOrderItems(orderId);
    } catch (err: any) {
      set({ error: err?.response?.data?.error || 'Error actualizando producto' });
      throw err;
    }
  },

  deleteOrderItem: async (orderId, itemId) => {
    try {
      await importTrackingService.deleteOrderItem(orderId, itemId);
      set((s) => ({ orderItems: s.orderItems.filter((i) => i.id !== itemId) }));
    } catch (err: any) {
      set({ error: err?.response?.data?.error || 'Error eliminando producto' });
    }
  },

  // ── Costs ──
  costBreakdown: null,
  isLoadingCosts: false,

  fetchCostBreakdown: async (orderId) => {
    set({ isLoadingCosts: true, error: null });
    try {
      const costs = await importTrackingService.getCostBreakdown(orderId);
      set({ costBreakdown: costs, isLoadingCosts: false });
    } catch (err: any) {
      set({ error: err?.response?.data?.error || 'Error cargando costos', isLoadingCosts: false });
    }
  },

  updateCosts: async (orderId, data) => {
    set({ error: null });
    try {
      const costs = await importTrackingService.updateCosts(orderId, data);
      set({ costBreakdown: costs });
    } catch (err: any) {
      set({ error: err?.response?.data?.error || 'Error actualizando costos' });
      throw err;
    }
  },

  // ── Documents ──
  documents: [],
  isLoadingDocuments: false,

  fetchDocuments: async (orderId) => {
    set({ isLoadingDocuments: true, error: null });
    try {
      const docs = await importTrackingService.getDocuments(orderId);
      set({ documents: docs, isLoadingDocuments: false });
    } catch (err: any) {
      set({
        error: err?.response?.data?.error || 'Error cargando documentos',
        isLoadingDocuments: false,
      });
    }
  },

  uploadDocument: async (orderId, file, type, name) => {
    set({ error: null });
    try {
      const doc = await importTrackingService.uploadDocument(orderId, file, type, name);
      set((s) => ({ documents: [doc, ...s.documents] }));
    } catch (err: any) {
      set({ error: err?.response?.data?.error || 'Error subiendo documento' });
      throw err;
    }
  },

  deleteDocument: async (orderId, docId) => {
    try {
      await importTrackingService.deleteDocument(orderId, docId);
      set((s) => ({ documents: s.documents.filter((d) => d.id !== docId) }));
    } catch (err: any) {
      set({ error: err?.response?.data?.error || 'Error eliminando documento' });
    }
  },

  // ── Tracking Events ──
  trackingEvents: [],
  isLoadingTracking: false,

  fetchTrackingEvents: async (orderId) => {
    set({ isLoadingTracking: true, error: null });
    try {
      const events = await importTrackingService.getTrackingEvents(orderId);
      set({ trackingEvents: events, isLoadingTracking: false });
    } catch (err: any) {
      set({
        error: err?.response?.data?.error || 'Error cargando seguimiento',
        isLoadingTracking: false,
      });
    }
  },

  addTrackingEvent: async (orderId, data) => {
    set({ error: null });
    try {
      const event = await importTrackingService.addTrackingEvent(orderId, data);
      set((s) => ({ trackingEvents: [event, ...s.trackingEvents] }));
      // Refresh order to get updated status
      await get().fetchOrder(orderId);
    } catch (err: any) {
      set({ error: err?.response?.data?.error || 'Error agregando evento' });
      throw err;
    }
  },

  // ── Audit Logs ──
  auditLogs: [],
  isLoadingAuditLogs: false,

  fetchAuditLogs: async (orderId) => {
    set({ isLoadingAuditLogs: true, error: null });
    try {
      const logs = await importTrackingService.getAuditLogs(orderId);
      set({ auditLogs: logs, isLoadingAuditLogs: false });
    } catch (err: any) {
      set({
        error: err?.response?.data?.error || 'Error cargando historial',
        isLoadingAuditLogs: false,
      });
    }
  },

  // ── Dashboard ──
  dashboardStats: null,
  importsByStatus: [],
  monthlyTrends: [],
  topSuppliers: [],
  costVarianceAlerts: [],
  isLoadingDashboard: false,

  fetchDashboard: async () => {
    set({ isLoadingDashboard: true, error: null });
    try {
      const [stats, byStatus, trends, suppliers, alerts] = await Promise.all([
        importTrackingService.getDashboardStats(),
        importTrackingService.getImportsByStatus(),
        importTrackingService.getMonthlyTrends(12),
        importTrackingService.getTopSuppliers(10),
        importTrackingService.getCostVarianceAlerts(),
      ]);
      set({
        dashboardStats: stats,
        importsByStatus: byStatus,
        monthlyTrends: trends,
        topSuppliers: suppliers,
        costVarianceAlerts: alerts,
        isLoadingDashboard: false,
      });
    } catch (err: any) {
      set({
        error: err?.response?.data?.error || 'Error cargando dashboard',
        isLoadingDashboard: false,
      });
    }
  },

  // ── Landed Cost Calculator ──
  landedCostResult: null,
  isCalculating: false,

  calculateLandedCost: async (data) => {
    set({ isCalculating: true, error: null });
    try {
      const result = await importTrackingService.calculateLandedCost(data);
      set({ landedCostResult: result, isCalculating: false });
      return result;
    } catch (err: any) {
      set({ error: err?.response?.data?.error || 'Error calculando costo', isCalculating: false });
      return null;
    }
  },

  clearLandedCostResult: () => set({ landedCostResult: null }),

  // ── Suppliers ──
  suppliers: [],
  isLoadingSuppliers: false,

  fetchSuppliers: async () => {
    set({ isLoadingSuppliers: true, error: null });
    try {
      const suppliers = await importTrackingService.getSuppliers();
      set({ suppliers, isLoadingSuppliers: false });
    } catch (err: any) {
      set({
        error: err?.response?.data?.error || 'Error cargando proveedores',
        isLoadingSuppliers: false,
      });
    }
  },

  createSupplier: async (data) => {
    set({ error: null });
    try {
      await importTrackingService.createSupplier(data);
      await get().fetchSuppliers();
    } catch (err: any) {
      set({ error: err?.response?.data?.error || 'Error creando proveedor' });
      throw err;
    }
  },

  updateSupplier: async (id: string, data: Partial<CreateImportSupplierInput>) => {
    set({ error: null });
    try {
      await importTrackingService.updateSupplier(id, data);
      await get().fetchSuppliers();
    } catch (err: any) {
      set({ error: err?.response?.data?.error || 'Error actualizando proveedor' });
      throw err;
    }
  },

  deleteSupplier: async (id: string) => {
    set({ error: null });
    try {
      await importTrackingService.deleteSupplier(id);
      await get().fetchSuppliers();
    } catch (err: any) {
      set({ error: err?.response?.data?.error || 'Error eliminando proveedor' });
      throw err;
    }
  },

  // ── Container Types ──
  containerTypes: [],
  isLoadingContainerTypes: false,

  fetchContainerTypes: async () => {
    set({ isLoadingContainerTypes: true, error: null });
    try {
      const types = await importTrackingService.getContainerTypes();
      set({ containerTypes: types, isLoadingContainerTypes: false });
    } catch (err: any) {
      set({
        error: err?.response?.data?.error || 'Error cargando tipos de contenedor',
        isLoadingContainerTypes: false,
      });
    }
  },

  createContainerType: async (data) => {
    set({ error: null });
    try {
      const ct = await importTrackingService.createContainerType(data);
      await get().fetchContainerTypes();
      return ct;
    } catch (err: any) {
      set({ error: err?.response?.data?.error || 'Error creando tipo de contenedor' });
      throw err;
    }
  },

  deleteContainerType: async (id) => {
    try {
      await importTrackingService.deleteContainerType(id);
      set((s) => ({ containerTypes: s.containerTypes.filter((ct) => ct.id !== id) }));
    } catch (err: any) {
      set({ error: err?.response?.data?.error || 'Error eliminando tipo de contenedor' });
    }
  },

  // ── Containers ──
  containers: [],
  currentContainer: null,
  isLoadingContainers: false,

  fetchOrderContainers: async (orderId) => {
    set({ isLoadingContainers: true, error: null });
    try {
      const containers = await importTrackingService.getOrderContainers(orderId);
      set({ containers, isLoadingContainers: false });
    } catch (err: any) {
      set({
        error: err?.response?.data?.error || 'Error cargando contenedores',
        isLoadingContainers: false,
      });
    }
  },

  fetchContainer: async (id) => {
    set({ isLoadingContainers: true, error: null });
    try {
      const container = await importTrackingService.getContainer(id);
      set({ currentContainer: container, isLoadingContainers: false });
    } catch (err: any) {
      set({
        error: err?.response?.data?.error || 'Error cargando contenedor',
        isLoadingContainers: false,
      });
    }
  },

  createContainer: async (orderId, data) => {
    set({ error: null });
    try {
      const container = await importTrackingService.createContainer(orderId, data);
      await get().fetchOrderContainers(orderId);
      return container;
    } catch (err: any) {
      set({ error: err?.response?.data?.error || 'Error creando contenedor' });
      throw err;
    }
  },

  updateContainer: async (id, data) => {
    set({ error: null });
    try {
      const updated = await importTrackingService.updateContainer(id, data);
      set({ currentContainer: updated });
    } catch (err: any) {
      set({ error: err?.response?.data?.error || 'Error actualizando contenedor' });
      throw err;
    }
  },

  deleteContainer: async (id) => {
    try {
      await importTrackingService.deleteContainer(id);
      set((s) => ({ containers: s.containers.filter((c) => c.id !== id) }));
    } catch (err: any) {
      set({ error: err?.response?.data?.error || 'Error eliminando contenedor' });
    }
  },

  // ── Container Items ──
  containerItems: [],
  isLoadingContainerItems: false,

  fetchContainerItems: async (containerId) => {
    set({ isLoadingContainerItems: true, error: null });
    try {
      const items = await importTrackingService.getContainerItems(containerId);
      set({ containerItems: items, isLoadingContainerItems: false });
    } catch (err: any) {
      set({
        error: err?.response?.data?.error || 'Error cargando items del contenedor',
        isLoadingContainerItems: false,
      });
    }
  },

  addContainerItem: async (containerId, data) => {
    set({ error: null });
    try {
      const item = await importTrackingService.addContainerItem(containerId, data);
      await get().fetchContainerItems(containerId);
      return item;
    } catch (err: any) {
      set({ error: err?.response?.data?.error || 'Error agregando item' });
      throw err;
    }
  },

  updateContainerItem: async (containerId, itemId, data) => {
    set({ error: null });
    try {
      await importTrackingService.updateContainerItem(containerId, itemId, data);
      await get().fetchContainerItems(containerId);
    } catch (err: any) {
      set({ error: err?.response?.data?.error || 'Error actualizando item' });
      throw err;
    }
  },

  deleteContainerItem: async (containerId, itemId) => {
    try {
      await importTrackingService.deleteContainerItem(containerId, itemId);
      set((s) => ({ containerItems: s.containerItems.filter((i) => i.id !== itemId) }));
    } catch (err: any) {
      set({ error: err?.response?.data?.error || 'Error eliminando item' });
    }
  },

  // ── Container Positions ──
  isSavingPositions: false,

  saveContainerPositions: async (containerId, data) => {
    set({ isSavingPositions: true, error: null });
    try {
      await importTrackingService.saveContainerPositions(containerId, data);
      set({ isSavingPositions: false });
    } catch (err: any) {
      set({
        error: err?.response?.data?.error || 'Error guardando posiciones',
        isSavingPositions: false,
      });
      throw err;
    }
  },

  // ── Auto Calculate ──
  autoCalculateResult: null,
  isAutoCalculating: false,

  autoCalculateImport: async (orderId, params) => {
    set({ isAutoCalculating: true, error: null, autoCalculateResult: null });
    try {
      const result = await importTrackingService.autoCalculate(orderId, params);
      set({ autoCalculateResult: result, isAutoCalculating: false });
      // Refresh order, costs and items to reflect new values
      await get().fetchOrder(orderId);
      await get().fetchCostBreakdown(orderId);
      await get().fetchOrderItems(orderId);
      return result;
    } catch (err: any) {
      set({
        error: err?.response?.data?.error || 'Error calculando orden',
        isAutoCalculating: false,
      });
      return null;
    }
  },

  clearAutoCalculateResult: () => set({ autoCalculateResult: null }),

  // ── Convert Quote to Order ──
  convertToOrder: async (orderId) => {
    set({ isLoading: true, error: null });
    try {
      await importTrackingService.convertToOrder(orderId);
      await get().fetchOrder(orderId);
      set({ isLoading: false });
    } catch (error: any) {
      set({ isLoading: false, error: error.message || 'Error al convertir cotización' });
      throw error;
    }
  },

  // ── Available Containers (Availability Map) ──
  availableContainers: [],
  portSummaries: [],
  loadingAvailableContainers: false,

  loadAvailableContainers: async () => {
    set({ loadingAvailableContainers: true, error: null });
    try {
      const containers = await importTrackingService.getAvailableContainers();
      set({ availableContainers: containers, loadingAvailableContainers: false });
    } catch (err: any) {
      set({
        error: err?.response?.data?.error || 'Error cargando contenedores disponibles',
        loadingAvailableContainers: false,
      });
    }
  },

  loadPortSummaries: async () => {
    set({ loadingAvailableContainers: true, error: null });
    try {
      const summaries = await importTrackingService.getAvailableContainersByPort();
      set({ portSummaries: summaries, loadingAvailableContainers: false });
    } catch (err: any) {
      set({
        error: err?.response?.data?.error || 'Error cargando resumen por puerto',
        loadingAvailableContainers: false,
      });
    }
  },

  // ── Error ──
  error: null,
  clearError: () => set({ error: null }),
}));
